import { useMyOpportunityList } from '@/features/opportunities/hooks';
import { useRegisterGAPageView } from '@/hooks';

import { MyOpportunitiesEmptyState } from '../empty-state';
import { MyOpportunitiesItems } from '../items';
import { StandardCard } from '@/components';
import { NotificationsCard } from '@/features/alerts';
import { useMemo } from 'react';
import { SellerAnalytics } from '../seller-analytics';
import { Spinner } from '@/components/ui/spinner';

export type MyOpportunitiesContentProps = {
  readonly onCreateOpportunity?: () => void;
};

export function MyOpportunitiesContent({ onCreateOpportunity }: MyOpportunitiesContentProps) {
  const { data: opportunities, isLoading } = useMyOpportunityList();
  useRegisterGAPageView('My opportunities list');

  const stats = useMemo(() => {
    if (!opportunities) return { liveCount: 0, eoisReceived: 0, totalViews: 0 };

    const visibleStatuses = ['pending', 'accepted', 'rejected'];

    const totalOffersCount = opportunities
      .flatMap((item) => item.offers)
      .filter((offer) => visibleStatuses.includes(offer.status)).length;

    return {
      liveCount: opportunities.filter((opp) => opp.isPublished === true).length,
      eoisReceived: totalOffersCount,
      totalViews: opportunities.reduce((sum, opp) => sum + (opp.stats?.opportunitiesViews ?? 0), 0),
    };
  }, [opportunities]);

  if (!opportunities) return null;

  return (
    <>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SellerAnalytics stats={stats} isLoading={isLoading} />
        <NotificationsCard context="seller" />
      </div>

      <StandardCard
        title="Your Opportunities"
        description="Here you can find all your opportunities and their current status."
      >
        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <Spinner className="text-primary-700 size-10" />
          </div>
        ) : opportunities?.length === 0 ? (
          <MyOpportunitiesEmptyState onCreateOpportunity={onCreateOpportunity} />
        ) : (
          <MyOpportunitiesItems opportunities={opportunities} />
        )}
      </StandardCard>
    </>
  );
}
